import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import InvoiceService from '../../services/InvoiceService';
import { PageHeader, Card, Button, Badge, Table, Tr, Td } from '../ui';

const money = (c, cur = 'cad') => new Intl.NumberFormat('en-CA', { style: 'currency', currency: (cur || 'cad').toUpperCase() }).format((c || 0) / 100);
const date = (d) => (d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const tone = { paid: 'good', issued: 'warn', draft: 'neutral', void: 'neutral', refunded: 'neutral' };

const InvoiceView = () => {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    InvoiceService.getInvoice(id).then(setInv).catch(() => setErr(true));
  }, [id]);

  if (err) return (
    <div className="tw-mx-auto tw-max-w-lg tw-py-10">
      <Card><div className="tw-text-center tw-text-content-muted">Invoice not found. <Link to="/billing" className="tw-text-brand">Back to billing</Link></div></Card>
    </div>
  );
  if (!inv) return <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;

  return (
    <div className="tw-mx-auto tw-max-w-2xl">
      <PageHeader
        title={`Invoice ${inv.invoice_number}`}
        subtitle={<>Issued {date(inv.issue_date)} · <Badge tone={tone[inv.status] || 'neutral'}>{inv.status}</Badge></>}
        actions={
          <>
            <Button variant="secondary" icon="fa-up-right-from-square" as="a" href={InvoiceService.invoiceHtmlUrl(inv.id)} target="_blank" rel="noreferrer">Open</Button>
            <Button icon="fa-file-pdf" as="a" href={InvoiceService.invoicePdfUrl(inv.id)} target="_blank" rel="noreferrer">Download PDF</Button>
          </>
        }
      />

      <Card>
        <div className="tw-flex tw-justify-between tw-text-sm">
          <div>
            <div className="tw-text-content-faint tw-text-xs tw-uppercase">Bill to</div>
            <div className="tw-font-semibold tw-text-heading">{inv.bill_to_name || '—'}</div>
            <div className="tw-text-content-muted">{inv.bill_to_email}</div>
          </div>
          <div className="tw-text-right">
            <div><span className="tw-text-content-faint">Due:</span> {date(inv.due_date)}</div>
            {inv.paid_date && <div><span className="tw-text-content-faint">Paid:</span> {date(inv.paid_date)}</div>}
          </div>
        </div>

        <div className="tw-mt-5">
          <Table head={['Description', 'Qty', 'Amount']}>
            {(inv.line_items || []).map((li, i) => (
              <Tr key={i}>
                <Td>{li.description}</Td>
                <Td>{li.quantity}</Td>
                <Td className="tw-text-right">{money(li.amount, inv.currency)}</Td>
              </Tr>
            ))}
          </Table>
        </div>

        <div className="tw-mt-4 tw-ml-auto tw-max-w-xs tw-space-y-1 tw-text-sm">
          <div className="tw-flex tw-justify-between tw-text-content-muted"><span>Subtotal</span><span>{money(inv.subtotal, inv.currency)}</span></div>
          {(inv.tax_lines || []).map((t, i) => (
            <div key={i} className="tw-flex tw-justify-between tw-text-content-muted">
              <span>{t.name}{t.type === 'percentage' ? ` (${t.rate}%)` : ''}</span>
              <span>{money(t.amount, inv.currency)}</span>
            </div>
          ))}
          <div className="tw-flex tw-justify-between tw-border-t tw-border-line tw-pt-1 tw-font-bold tw-text-heading">
            <span>Total</span><span>{money(inv.total, inv.currency)}</span>
          </div>
        </div>

        {inv.notes && <p className="tw-mt-4 tw-text-sm tw-text-content-muted">{inv.notes}</p>}
      </Card>

      <div className="tw-mt-4 tw-text-center">
        <Link to="/billing" className="tw-text-sm tw-text-brand">← Back to billing</Link>
      </div>
    </div>
  );
};

export default InvoiceView;
